import { useEffect } from 'react';
import { ShieldCheck, AlertTriangle, Clock, Archive } from 'lucide-react';
import { useDecisionsStore } from '../../../stores/prioritization/decisionsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import {
  HEALTH_CONFIG,
  summarizeHealth,
  computeHealthStatus,
} from '../../../lib/decisionHealth';
import type { DecisionHealthStatus } from '../../../types/prioritization/decisions';

const STATUS_ICON: Record<DecisionHealthStatus, React.ElementType> = {
  fresh:    ShieldCheck,
  aging:    Clock,
  stale:    AlertTriangle,
  orphaned: Archive,
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(iso));
}

export function DecisionHealthDashboard() {
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const itemPools = useItemPoolsStore((s) => s.itemPools);

  useEffect(() => {
    if (decisions.length === 0) fetchDecisions();
  }, []);

  const summary = summarizeHealth(decisions);

  const staleAndOrphaned = decisions.filter(
    (d) => (d.healthStatus ?? computeHealthStatus(d)) === 'stale' ||
           (d.healthStatus ?? computeHealthStatus(d)) === 'orphaned',
  );

  const aging = decisions.filter(
    (d) => (d.healthStatus ?? computeHealthStatus(d)) === 'aging',
  );

  // Build per-pool stats
  const poolStats = itemPools.map((pool) => {
    const poolDecisions = decisions.filter((d) => d.itemPoolId === pool.id);
    const ps = summarizeHealth(poolDecisions);
    return { pool, ...ps };
  });

  function getPoolName(itemPoolId: string | null | undefined): string {
    if (!itemPoolId) return '—';
    return itemPools.find((p) => p.id === itemPoolId)?.name ?? '—';
  }

  const segments: { key: DecisionHealthStatus; count: number }[] = [
    { key: 'fresh',    count: summary.fresh },
    { key: 'aging',    count: summary.aging },
    { key: 'stale',    count: summary.stale },
    { key: 'orphaned', count: summary.orphaned },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <ShieldCheck className="h-7 w-7 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">Decision Health</h2>
        </div>
        <p className="text-sm text-gray-500">
          Track rationale coverage and review cadence across all recorded decisions.
        </p>
      </div>

      {/* Coverage Score + Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-end gap-4 mb-4">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
              Rationale Coverage
            </p>
            <p className="text-5xl font-bold text-gray-900">{summary.coverageScore}%</p>
          </div>
          <p className="text-sm text-slate-500 mb-2">
            {summary.total - summary.orphaned} of {summary.total} decisions have documented rationale
          </p>
        </div>

        {/* Stacked bar */}
        {summary.total > 0 && (
          <div className="flex h-3 w-full rounded-full overflow-hidden gap-px">
            {segments.map(({ key, count }) => {
              if (count === 0) return null;
              const pct = (count / summary.total) * 100;
              return (
                <div
                  key={key}
                  title={`${HEALTH_CONFIG[key].label}: ${count}`}
                  className={`${HEALTH_CONFIG[key].dot} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3">
          {segments.map(({ key, count }) => {
            const cfg = HEALTH_CONFIG[key];
            const Icon = STATUS_ICON[key];
            return (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                <Icon className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-600">
                  {cfg.label} <span className="font-semibold text-slate-800">{count}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Needs Attention */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          Needs Attention
          {staleAndOrphaned.length > 0 && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
              {staleAndOrphaned.length}
            </span>
          )}
        </h3>
        <p className="text-xs text-slate-400 mb-4">Stale (past review date) and orphaned (no rationale) decisions.</p>

        {staleAndOrphaned.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No stale or orphaned decisions — rationale is up to date.</p>
        ) : (
          <DecisionTable rows={staleAndOrphaned} getPoolName={getPoolName} />
        )}
      </div>

      {/* Aging */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          Aging
          {aging.length > 0 && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
              {aging.length}
            </span>
          )}
        </h3>
        <p className="text-xs text-slate-400 mb-4">Decisions approaching their review date or with incomplete rationale.</p>

        {aging.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No aging decisions.</p>
        ) : (
          <DecisionTable rows={aging} getPoolName={getPoolName} />
        )}
      </div>

      {/* Health by Focus List */}
      {poolStats.filter((ps) => ps.total > 0).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Health by Focus List</h3>
          <div className="space-y-3">
            {poolStats
              .filter((ps) => ps.total > 0)
              .map(({ pool, total, fresh, aging: ag, stale, orphaned, coverageScore }) => (
                <div key={pool.id} className="flex items-center gap-3">
                  <div className="w-36 text-sm text-slate-700 truncate" title={pool.name}>
                    {pool.name}
                  </div>
                  <div className="flex-1 h-2.5 rounded-full overflow-hidden flex gap-px bg-slate-100">
                    {[
                      { key: 'fresh' as const,    count: fresh },
                      { key: 'aging' as const,     count: ag },
                      { key: 'stale' as const,     count: stale },
                      { key: 'orphaned' as const,  count: orphaned },
                    ].map(({ key, count }) => {
                      if (count === 0 || total === 0) return null;
                      return (
                        <div
                          key={key}
                          title={`${HEALTH_CONFIG[key].label}: ${count}`}
                          className={`${HEALTH_CONFIG[key].dot}`}
                          style={{ width: `${(count / total) * 100}%` }}
                        />
                      );
                    })}
                  </div>
                  <div className="w-16 text-right text-xs text-slate-500">
                    {coverageScore}% covered
                  </div>
                  <div className="w-10 text-right text-xs text-slate-400">
                    {total} dec
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DecisionTable({
  rows,
  getPoolName,
}: {
  rows: ReturnType<typeof useDecisionsStore.getState>['decisions'];
  getPoolName: (id: string | null | undefined) => string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead>
          <tr>
            {['Title', 'Impact', 'Focus List', 'Decided', 'Status'].map((h) => (
              <th
                key={h}
                className="py-2 pr-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wide"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((d) => {
            const health = d.healthStatus ?? computeHealthStatus(d);
            const hcfg = HEALTH_CONFIG[health];
            return (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="py-2 pr-4 text-sm text-slate-800 max-w-[220px] truncate">
                  {d.title}
                </td>
                <td className="py-2 pr-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      d.impact === 'high'
                        ? 'bg-red-100 text-red-700'
                        : d.impact === 'medium'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {d.impact}
                  </span>
                </td>
                <td className="py-2 pr-4 text-sm text-slate-600 max-w-[140px] truncate">
                  {getPoolName(d.itemPoolId)}
                </td>
                <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">
                  {formatDate(d.decidedAt)}
                </td>
                <td className="py-2 pr-4">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${hcfg.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${hcfg.dot}`} />
                    {hcfg.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
